/**
 * A typed filter that commits to the URL once the person pauses.
 *
 * Committing every keystroke pushed a history entry and fired a request per
 * character: typing "claude-opus" left eleven entries behind the back button.
 * The draft lives here, the URL gets the settled value, and a value that is not
 * yet valid (half a timestamp) is held back rather than sent to be rejected.
 */

import { useEffect, useRef, useState } from "react";

import { normalizeUtc } from "@pricing/contracts";

const SETTLE_MS = 300;

export interface TextFilterProps {
  readonly label: string;
  readonly value: string;
  readonly onCommit: (value: string) => void;
  readonly placeholder?: string;
  readonly type?: "search" | "text";
  /** Returns a problem to show, or null when the draft may be committed. */
  readonly validate?: (value: string) => string | null;
}

export function TextFilter(props: TextFilterProps): React.JSX.Element {
  const [draft, setDraft] = useState(props.value);
  const committed = useRef(props.value);
  const onCommit = useRef(props.onCommit);
  onCommit.current = props.onCommit;

  // Follow the URL when it changes underneath the field (the back button, a
  // cleared filter), but not when it is only catching up with this draft.
  useEffect(() => {
    if (props.value !== committed.current) {
      committed.current = props.value;
      setDraft(props.value);
    }
  }, [props.value]);

  const problem = props.validate?.(draft) ?? null;

  useEffect(() => {
    if (draft === committed.current || problem !== null) return;
    const timer = setTimeout(() => {
      committed.current = draft;
      onCommit.current(draft);
    }, SETTLE_MS);
    return () => clearTimeout(timer);
  }, [draft, problem]);

  return (
    <label className="field">
      {props.label}
      <input
        type={props.type ?? "text"}
        value={draft}
        placeholder={props.placeholder}
        aria-invalid={problem !== null}
        onChange={(event) => setDraft(event.target.value)}
      />
      {problem !== null ? <span className="field__problem">{problem}</span> : null}
    </label>
  );
}

/**
 * Accepts an empty field or anything the API's own timestamp parser accepts:
 * a date (read as midnight UTC) or an ISO-8601 time.
 */
export function validateInstant(value: string): string | null {
  if (value.trim() === "") return null;
  try {
    normalizeUtc(value);
    return null;
  } catch {
    return "Use a date (2026-09-01) or a UTC time (2026-09-01T12:00Z).";
  }
}
